import { useEffect, useState } from "react";
import api from "../lib/api";
import { useIG } from "../lib/igContext";
import { Instagram, Plus, Trash2, RefreshCw, Link2, AlertTriangle, CheckCircle2, Loader2, X, Edit3 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  manual: { label: "Manuel", color: "#888", desc: "Saisie manuelle des stats" },
  connected: { label: "Connecté", color: "#10B981", desc: "Sync Meta API actif" },
  token_expired: { label: "Token expiré", color: "#F59E0B", desc: "Reconnexion requise" },
  api_error: { label: "Erreur API", color: "#EF4444", desc: "Erreur Meta API" },
  disconnected: { label: "Déconnecté", color: "#666", desc: "Inactif" },
};

export default function InstagramAccounts() {
  const { accounts, refresh } = useIG();
  const [models, setModels] = useState([]);
  const [oauth, setOauth] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const refreshAll = async () => {
    await refresh();
    try { setModels(await api.crmListModels()); } catch {}
    try { setOauth(await api.igOauthStatus()); } catch {}
  };

  useEffect(() => { refreshAll(); }, []);

  const remove = async (id, handle) => {
    if (!confirm(`Supprimer @${handle} et toutes ses données ?`)) return;
    try { await api.igDeleteAccount(id); toast.success("Compte supprimé"); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const connect = async (id) => {
    try {
      const r = await api.igOauthStart(id);
      window.location.href = r.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OAuth non disponible");
    }
  };

  const sync = async (id) => {
    try { await api.igSync(id); toast.success("Sync effectué"); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync impossible"); }
  };

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="instagram-accounts-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// COMPTES INSTAGRAM</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Connexions IG</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">Connecte tes comptes Instagram Business via l'API officielle Meta. Multi-comptes, tokens chiffrés, sync auto toutes les 6h.</p>
        </div>
        <button data-testid="add-ig-btn" onClick={() => setShowAdd(true)}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" /> Ajouter un compte
        </button>
      </header>

      {/* OAuth status banner */}
      <div className={`border rounded-sm p-4 ${oauth?.configured ? "border-green-500/30 bg-green-500/5" : "border-orange-500/30 bg-orange-500/5"}`} data-testid="oauth-banner">
        <div className="flex items-start gap-3">
          {oauth?.configured ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="text-sm font-medium text-white mb-1">
              {oauth?.configured ? "OAuth Meta configuré ✓" : "OAuth Meta en attente de configuration"}
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
              {oauth?.configured
                ? "Tu peux connecter tes comptes via le bouton 'Connecter avec Meta'."
                : "Pour l'instant, ajoute tes comptes en mode manuel et saisis les stats à la main. Quand tu auras tes clés Meta App, ajoute-les dans /app/backend/.env (META_APP_ID, META_APP_SECRET) et restart le backend."}
            </div>
            {oauth?.redirect_uri && (
              <div className="text-[10px] font-mono text-zinc-600 mt-2">
                Callback à enregistrer : <span className="text-[#CCFF00]">{oauth.redirect_uri}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.length === 0 ? (
          <div className="md:col-span-2 border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
            <Instagram className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-zinc-500 text-sm mb-4">Aucun compte Instagram connecté.</div>
            <button onClick={() => setShowAdd(true)} className="text-[#CCFF00] text-sm font-mono uppercase tracking-wider hover:underline">
              + Ajouter ton premier compte
            </button>
          </div>
        ) : accounts.map((a, i) => {
          const meta = STATUS_META[a.status] || STATUS_META.manual;
          const model = models.find(m => m.id === a.model_id);
          return (
            <div key={a.id} data-testid={`ig-account-${i}`} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {(a.display_name || a.handle).slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-display text-lg text-white truncate">{a.display_name || a.handle}</div>
                    <span className="text-[9px] font-mono uppercase px-2 py-0.5 rounded-sm" style={{ backgroundColor: meta.color + "20", color: meta.color }}>{meta.label}</span>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500">@{a.handle}</div>
                  {model && <div className="text-[10px] text-zinc-600 mt-0.5">→ {model.name}</div>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <Stat label="Abonnés" v={a.followers} />
                <Stat label="Posts" v={a.posts_count} />
                <Stat label="Suit" v={a.following} />
              </div>

              {a.last_sync && (
                <div className="text-[10px] font-mono text-zinc-600 mb-3">
                  Dernier sync : {new Date(a.last_sync).toLocaleString("fr-FR")}
                </div>
              )}
              {a.error_message && (
                <div className="text-xs text-red-400 mb-3 truncate">⚠ {a.error_message}</div>
              )}

              <div className="flex gap-2 flex-wrap">
                {oauth?.configured && a.status !== "connected" && (
                  <button onClick={() => connect(a.id)} data-testid={`connect-${i}`}
                    className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 bg-[#CCFF00] text-black rounded-sm hover:bg-[#B3E600] flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> Connecter Meta
                  </button>
                )}
                {a.status === "connected" && (
                  <button onClick={() => sync(a.id)} data-testid={`sync-${i}`}
                    className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-[#CCFF00]/30 text-[#CCFF00] rounded-sm hover:bg-[#CCFF00]/10 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Sync now
                  </button>
                )}
                <button onClick={() => setEditing(a)} data-testid={`edit-${i}`}
                  className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border border-[#1E1E24] text-zinc-400 rounded-sm hover:border-white/30 hover:text-white flex items-center gap-1">
                  <Edit3 className="w-3 h-3" /> Modifier
                </button>
                <button onClick={() => remove(a.id, a.handle)} data-testid={`delete-${i}`}
                  className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 text-zinc-500 hover:text-red-400 flex items-center gap-1 ml-auto">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSuccess={refreshAll} models={models} />}
      {editing && <EditModal account={editing} onClose={() => setEditing(null)} onSuccess={refreshAll} models={models} />}
    </div>
  );
}

const Stat = ({ label, v }) => (
  <div className="border border-[#1E1E24] bg-[#070708] rounded-sm p-2 text-center">
    <div className="font-mono text-lg text-white">{(v || 0).toLocaleString("fr-FR")}</div>
    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
  </div>
);

function AddModal({ onClose, onSuccess, models }) {
  const [f, setF] = useState({ handle: "", display_name: "", bio: "", model_id: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.igCreateAccount(f); toast.success("Compte ajouté"); onSuccess(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Nouveau compte Instagram" testid="add-ig-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Handle (sans @)" testid="add-ig-handle" required value={f.handle} onChange={v => setF({...f, handle: v})} placeholder="lina_official" />
        <Input label="Nom affiché" testid="add-ig-name" value={f.display_name} onChange={v => setF({...f, display_name: v})} placeholder="Lina" />
        <Input label="Bio" testid="add-ig-bio" value={f.bio} onChange={v => setF({...f, bio: v})} />
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Modèle CRM associé</label>
          <select data-testid="add-ig-model" value={f.model_id} onChange={e => setF({...f, model_id: e.target.value})}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
            <option value="">Aucun</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <button type="submit" disabled={loading} data-testid="add-ig-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "Création..." : "Ajouter"}
        </button>
      </form>
    </Modal>
  );
}

function EditModal({ account, onClose, onSuccess, models }) {
  const [f, setF] = useState({
    display_name: account.display_name || "",
    bio: account.bio || "",
    avatar_url: account.avatar_url || "",
    followers: account.followers || 0,
    following: account.following || 0,
    posts_count: account.posts_count || 0,
    model_id: account.model_id || "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const data = { ...f, followers: Number(f.followers), following: Number(f.following), posts_count: Number(f.posts_count) };
      await api.igUpdateAccount(account.id, data);
      toast.success("Mis à jour"); onSuccess(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title={`Modifier @${account.handle}`} testid="edit-ig-modal">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Nom affiché" testid="edit-name" value={f.display_name} onChange={v => setF({...f, display_name: v})} />
        <Input label="Bio" testid="edit-bio" value={f.bio} onChange={v => setF({...f, bio: v})} />
        <Input label="URL avatar" testid="edit-avatar" value={f.avatar_url} onChange={v => setF({...f, avatar_url: v})} />
        <div className="grid grid-cols-3 gap-2">
          <Input label="Abonnés" testid="edit-followers" type="number" value={f.followers} onChange={v => setF({...f, followers: v})} />
          <Input label="Suit" testid="edit-following" type="number" value={f.following} onChange={v => setF({...f, following: v})} />
          <Input label="Posts" testid="edit-posts" type="number" value={f.posts_count} onChange={v => setF({...f, posts_count: v})} />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Modèle CRM</label>
          <select data-testid="edit-model" value={f.model_id} onChange={e => setF({...f, model_id: e.target.value})}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
            <option value="">Aucun</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <button type="submit" disabled={loading} data-testid="edit-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "..." : "Enregistrer"}
        </button>
      </form>
    </Modal>
  );
}

const Modal = ({ onClose, title, testid, children }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-xl text-white">{title}</div>
        <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Input = ({ label, testid, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <input data-testid={testid} type={type} required={required} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
